import { useState, useEffect, useCallback } from 'react';
import { auth as firebaseAuth } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithCustomToken,
  onAuthStateChanged,
  signOut
} from 'firebase/auth';
import { postJson } from '../utils/apiClient';

/**
 * Hook for managing authentication state and operations
 */
export default function useAuth() {
  const [token, setToken] = useState(localStorage.getItem('kanto-token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('kanto-user')));
  const [authError, setAuthError] = useState(null);
  const [isFirebaseAuthenticated, setIsFirebaseAuthenticated] = useState(false);
  // True once onAuthStateChanged has fired at least once. Lets the UI
  // distinguish "we don't know yet" from "we know there's no user".
  const [authResolved, setAuthResolved] = useState(false);

  // Handle logout
  const handleLogout = useCallback(() => {
    setToken(null);
    setUser(null);
    setIsFirebaseAuthenticated(false);
    signOut(firebaseAuth);
    ['kanto-token', 'kanto-selected-list', 'kanto-cache', 'kanto-user'].forEach(k => localStorage.removeItem(k));
  }, []);

  // Keep local token/user in sync with Firebase Auth.
  // We only clear local auth here when Firebase explicitly says "no user" AND
  // the user wasn't already authed locally — otherwise an offline boot that
  // can't reach Firebase would log the user out.
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      if (fbUser) {
        try {
          const idToken = await fbUser.getIdToken(false);
          localStorage.setItem('kanto-token', idToken);
          setToken(idToken);
          const loggedInUser = { id: fbUser.uid, email: fbUser.email };
          localStorage.setItem('kanto-user', JSON.stringify(loggedInUser));
          setUser(loggedInUser);
        } catch (_) {
          // Token fetch failed; leave existing token in place.
        }
      }
      setAuthResolved(true);
    });
    return () => unsubscribeAuth();
  }, []);

  // Authenticate with Firebase using custom token
  useEffect(() => {
    const authenticateWithFirebase = async () => {
      if (token && !isFirebaseAuthenticated) {
        try {
          const { data } = await postJson(`/auth/firebase-token`, {});
          await signInWithCustomToken(firebaseAuth, data.firebaseToken);
          setIsFirebaseAuthenticated(true);
        } catch (error) {
          console.error("Firebase auth failed:", error);
        }
      }
    };
    authenticateWithFirebase();
  }, [token, isFirebaseAuthenticated]);

  // Register new user
  const handleRegister = async (email, password) => {
    setAuthError(null);
    try {
      const cred = await createUserWithEmailAndPassword(firebaseAuth, email, password);
      const idToken = await cred.user.getIdToken();
      localStorage.setItem('kanto-token', idToken);
      const newUser = { id: cred.user.uid, email: cred.user.email };
      localStorage.setItem('kanto-user', JSON.stringify(newUser));
      setUser(newUser);
      setToken(idToken);
    } catch (error) {
      setAuthError(error.message || 'Registration failed. Please try again.');
      console.error(error);
    }
  };

  // Login existing user
  const handleLogin = async (email, password) => {
    setAuthError(null);
    try {
      const cred = await signInWithEmailAndPassword(firebaseAuth, email, password);
      const idToken = await cred.user.getIdToken();
      localStorage.setItem('kanto-token', idToken);
      const loggedInUser = { id: cred.user.uid, email: cred.user.email };
      localStorage.setItem('kanto-user', JSON.stringify(loggedInUser));
      setUser(loggedInUser);
      setToken(idToken);
    } catch (error) {
      setAuthError(error.message || 'Login failed. Please check your credentials.');
      console.error(error);
    }
  };

  // Send password reset email
  const handleForgotPassword = async (email) => {
    try {
      await sendPasswordResetEmail(firebaseAuth, email);
      alert('If an account exists for that email, a reset link has been sent.');
    } catch (e) {
      alert(e.message || 'Failed to send reset email');
    }
  };

  // Update user state (for real-time listener updates)
  const updateUser = useCallback((updater) => {
    setUser(currentUser => {
      const updatedUser = typeof updater === 'function' ? updater(currentUser) : updater;
      if (updatedUser) {
        localStorage.setItem('kanto-user', JSON.stringify(updatedUser));
      }
      return updatedUser;
    });
  }, []);

  return {
    token,
    user,
    authError,
    isFirebaseAuthenticated,
    authResolved,
    handleLogin,
    handleRegister,
    handleLogout,
    handleForgotPassword,
    updateUser,
    setAuthError
  };
}
